import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';

import {
  type Ambiente,
  type AmbienteGovbr,
  type DadosAmbiente,
  type FiltroTentativas,
  type PaginaTentativas,
  type ResultadoTeste,
} from './contrato';

/**
 * A camada de dados do conector gov.br.
 *
 * ESTA E A PRIMEIRA TELA DO SISTEMA A BUSCAR DADOS. Antes dela nao havia um
 * `useQuery` em apps/web/src, e o padrao que ficar aqui vira o padrao das telas
 * das fases seguintes. Duas decisoes justificam o arquivo existir separado dos
 * componentes:
 *
 * 1. NENHUM COMPONENTE CHAMA `chamarApi` DIRETO. O componente pede um gancho e
 *    recebe estado; quem conhece caminho, metodo e chave de cache e so este
 *    arquivo. Trocar rota da API passa a ser uma edicao num lugar so.
 * 2. AS CHAVES DE CACHE MORAM AQUI, JUNTO DE QUEM AS INVALIDA. Chave repetida em
 *    dois arquivos e como o cache para de invalidar sem ninguem perceber — a
 *    gravacao limpa `['govbr','ambientes']` e a leitura observa
 *    `['govbr', 'ambientes']`, e sao chaves diferentes.
 *
 * SOBRE A RETENTATIVA. O cliente global tenta uma vez de novo
 * (nucleo/cliente-consultas.ts:12-20), o que e certo para rede instavel e errado
 * para 401 e 403: a resposta seria identica, e o unico efeito seria dobrar a
 * espera antes de a tela dizer o que houve. Por isso `naoInsistirEmRecusa`.
 */

export const CHAVES = {
  ambientes: ['govbr', 'ambientes'] as const,
  tentativas: (filtro: FiltroTentativas) => ['govbr', 'tentativas', filtro] as const,
};

/**
 * 401 e 403 nao se resolvem tentando de novo: o 401 pede entrar de novo — e o
 * cliente da API ja renovou o token por conta antes de desistir
 * (nucleo/auth/cliente-api.ts:76-80) — e o 403 pede outro caminho.
 */
function naoInsistirEmRecusa(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroApi && (erro.pedeEntrarDeNovo || erro.pedeOutroCaminho)) {
    return false;
  }

  return tentativas < 1;
}

/**
 * `habilitado` existe por causa do papel, nao por conveniencia.
 *
 * A tela confere com `podeVerConector` antes de renderizar qualquer coisa, e
 * quem nao tem `ADMIN` recebe a recusa sem que uma requisicao saia. Sem este
 * parametro a consulta dispararia assim mesmo — os ganchos sao chamados antes do
 * retorno antecipado, porque a ordem deles nao pode variar entre renders — e o
 * `GESTOR_ADMIN` colecionaria 403 no log da API a cada visita a uma tela que ele
 * ja sabia que nao alcanca.
 */
export function useAmbientes(habilitado = true): UseQueryResult<AmbienteGovbr[], ErroApi> {
  return useQuery<AmbienteGovbr[], ErroApi>({
    queryKey: CHAVES.ambientes,
    queryFn: ({ signal }) => chamarApi<AmbienteGovbr[]>('/govbr/ambientes', { sinal: signal }),
    enabled: habilitado,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * O historico, paginado no servidor (CLAUDE.md, "Regras de API", item 6).
 *
 * `placeholderData` mantem a pagina anterior visivel enquanto a proxima chega:
 * sem ele, cada troca de pagina apaga a tabela e monta o esqueleto de novo, e a
 * pagina inteira salta de altura. O estado de carregando continua existindo — ele
 * e o da PRIMEIRA busca, que e quando de fato nao ha o que mostrar.
 */
export function useTentativas(
  filtro: FiltroTentativas,
  habilitado = true,
): UseQueryResult<PaginaTentativas, ErroApi> {
  return useQuery<PaginaTentativas, ErroApi>({
    queryKey: CHAVES.tentativas(filtro),
    queryFn: ({ signal }) => {
      const parametros = new URLSearchParams({
        pagina: String(filtro.pagina),
        tamanho: String(filtro.tamanho),
      });

      if (filtro.ambiente) {
        parametros.set('ambiente', filtro.ambiente);
      }

      return chamarApi<PaginaTentativas>(`/govbr/tentativas?${parametros.toString()}`, {
        sinal: signal,
      });
    },
    enabled: habilitado,
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

export interface GravacaoDeAmbiente {
  ambiente: Ambiente;
  dados: DadosAmbiente;
}

/**
 * Grava a parametrizacao de um ambiente.
 *
 * A resposta do PUT e o ambiente ja gravado, entao ela entra direto no cache em
 * vez de disparar uma nova leitura: e o mesmo dado, e uma ida a rede a menos
 * entre salvar e ver salvo.
 */
export function useGravarAmbiente(): UseMutationResult<AmbienteGovbr, ErroApi, GravacaoDeAmbiente> {
  const consultas = useQueryClient();

  return useMutation<AmbienteGovbr, ErroApi, GravacaoDeAmbiente>({
    mutationFn: ({ ambiente, dados }) =>
      chamarApi<AmbienteGovbr>(`/govbr/ambientes/${ambiente}`, {
        metodo: 'PUT',
        corpo: dados,
      }),
    onSuccess: (gravado) => {
      consultas.setQueryData<AmbienteGovbr[]>(CHAVES.ambientes, (atuais) =>
        atuais?.map((item) => (item.ambiente === gravado.ambiente ? gravado : item)),
      );
    },
  });
}

/**
 * Dispara o teste de conexao.
 *
 * Duas invalidacoes, e as duas sao necessarias: o teste grava uma linha em
 * `govbr_tentativas` (o historico envelhece) e atualiza `ultima_verificacao_em`
 * e `ultima_verificacao_ok` na linha do ambiente (o cabecalho envelhece). Sem a
 * segunda, a tela mostraria o resultado do teste que acabou de rodar ao lado de
 * uma data antiga.
 *
 * `sucesso: false` NAO e erro desta mutacao: emissor fora do ar responde 200 com
 * o motivo legivel (criterio E4.3). Quem trata `onError` aqui trata falha de
 * rede e recusa de acesso — nunca "o emissor nao respondeu".
 */
export function useTestarConexao(): UseMutationResult<ResultadoTeste, ErroApi, Ambiente> {
  const consultas = useQueryClient();

  return useMutation<ResultadoTeste, ErroApi, Ambiente>({
    mutationFn: (ambiente) =>
      chamarApi<ResultadoTeste>(`/govbr/ambientes/${ambiente}/testar`, { metodo: 'POST' }),
    onSuccess: () => {
      void consultas.invalidateQueries({ queryKey: CHAVES.ambientes });
      void consultas.invalidateQueries({ queryKey: ['govbr', 'tentativas'] });
    },
  });
}
